import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { DialogMessage } from '../service/dialog-message.service';
import { JwtHelperService } from '../service/jwthelper.service';
import { PWUtils } from '../shared';
import {
  IgnoreJwtHttpInterceptorHeader, IJwtHttpIntConfig, JWT_HTTP_INT_CONFIG
} from './jwt-http-int-config';

@Injectable()
export class JwtHttpInterceptor implements HttpInterceptor {
  private jwtHelper = new JwtHelperService();

  constructor(
    private dialogMessage: DialogMessage,
    @Inject(JWT_HTTP_INT_CONFIG) private jwtHttpIntConfig: IJwtHttpIntConfig
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url === this.jwtHttpIntConfig.loginUrl ||
      request.url === this.jwtHttpIntConfig.refreshTokenUrl ||
      !PWUtils.isNotEmpty(this.jwtHttpIntConfig.authenticatedUser) ||
      request.url === this.jwtHttpIntConfig.logoutApiUrl ||
      request.headers.has(IgnoreJwtHttpInterceptorHeader)
    ) {
      if (request.headers.has(IgnoreJwtHttpInterceptorHeader)) {
        request = request.clone({ headers: request.headers.delete(IgnoreJwtHttpInterceptorHeader) });
      }
      // Prevent infinite intercept loop!
      return next.handle(request);
    }
    const token = this.jwtHttpIntConfig.authenticatedUser?.token;
    if (token && this.jwtHelper.isTokenExpired(token)) {
      return this.jwtHttpIntConfig.refreshToken().pipe(
        switchMap((res) => {
          if (!res || !res.token) {
            this.jwtHttpIntConfig.logout();
            return EMPTY;
          }

          return next.handle(this.addValuesToHeader(request));
        }), catchError((err: HttpErrorResponse) => {
          this.handleError(err);
          return EMPTY;
        })
      );
    } else {
      return next.handle(this.addValuesToHeader(request)).pipe(catchError((err: HttpErrorResponse) => {
        this.handleError(err);
        if ([0, 401].includes(err.status)) {
          return EMPTY;
        }
        return throwError(err);
      }));
    }
  }

  private handleError(err: HttpErrorResponse) {
    if ([0, 401].includes(err.status)) {
      this.dialogMessage.showErrorMessage('Please login again!');
      this.jwtHttpIntConfig.logout();
    } else if (err.status === 403) {
      this.dialogMessage.showErrorMessage('You do not have access permission');
    } else {
      this.dialogMessage.showErrorMessage(err.error?.message || err.error?.error || 'Oops! Something error');
    }
    console.error(err);
  }

  private addValuesToHeader(request: HttpRequest<any>): HttpRequest<any> {
    let headers = request.headers.set('Authorization', PWUtils.getBearerToken(this.jwtHttpIntConfig.authenticatedUser?.token ?? ''));
    headers = headers.set('CurrentTimezone', PWUtils.currentTimezone().toString());

    return request.clone({ headers });
  }
}
